import { useCallback, useMemo, useState } from "react";
import { ButtonKind, ButtonSize, ButtonVariant } from "@viagogo/core/ui";
import type { ISiteMessage } from "src/http/types";
import { Content, useContent } from "src/shared/components/content";
import { useAppContext } from "src/shared/context/AppContext";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { EmailOptInContentView } from "./EmailOptInContentView";
import { EmailOptInModal } from "./EmailOptInModal";
import { BannerContainer, BannerTitle, EmailOptInButton, EmailOptInButtonTitle } from "./styled-components";

interface EmailOptInBannerProps {
  readonly email?: string | undefined;
  readonly privacyPolicyMessage?: ISiteMessage | undefined;
  readonly onEmailOptIn?: () => void;
  /** Fired once when the banner scrolls into the viewport — the control-arm participation trigger for exp 103701. */
  readonly onInView?: (() => void) | undefined;
}

export const EmailOptInBanner = (props: EmailOptInBannerProps) => {
  const appContext = useAppContext();
  const { email, privacyPolicyMessage, onEmailOptIn, onInView } = props;
  const [isFormOpen, setIsFormOpen] = useState(false);

  const title = useContent("cid$#3869389#"); // "Stay in the loop with the hottest events"

  const handleEmailOptInClick = useCallback(() => {
    setIsFormOpen(true);
  }, []);

  const handleEmailOptIn = useCallback(() => {
    setIsFormOpen(false);
    onEmailOptIn?.();
  }, [onEmailOptIn]);

  const handleModalClose = useCallback(() => {
    setIsFormOpen(false);
  }, []);

  const contentView = useMemo(
    () => (
      <EmailOptInContentView
        email={email}
        privacyPolicyMessage={privacyPolicyMessage}
        onEmailSignUp={handleEmailOptIn}
      />
    ),
    [email, handleEmailOptIn, privacyPolicyMessage],
  );

  return (
    <div>
      {onInView && <InViewTracker onInView={onInView} />}
      <BannerContainer>
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src={`${appContext.env["REACT_APP_ASSETS_URL"] ?? ""}/img/home/email_opt_in_image-1.jpg`}
            alt=""
            style={{
              width: "100px",
              height: "60px",
              borderRadius: "200px",
              border: "2px solid white",
              objectFit: "cover",
              zIndex: 3,
            }}
          />
          <img
            src={`${appContext.env["REACT_APP_ASSETS_URL"] ?? ""}/img/home/email_opt_in_image-2.jpg`}
            alt=""
            style={{
              width: "100px",
              height: "60px",
              borderRadius: "200px",
              border: "2px solid white",
              marginInlineStart: "-33px",
              objectFit: "cover",
              zIndex: 2,
            }}
          />
          <img
            src={`${appContext.env["REACT_APP_ASSETS_URL"] ?? ""}/img/home/email_opt_in_image-3.jpg`}
            alt=""
            style={{
              width: "100px",
              height: "60px",
              borderRadius: "200px",
              border: "2px solid white",
              marginInlineStart: "-33px",
              objectFit: "cover",
              zIndex: 1,
            }}
          />
        </div>
        <BannerTitle>
          <Content id="cid$#3869382#" /> {/* "Discover when your favorite artists are on tour" */}
        </BannerTitle>
        <EmailOptInButton
          kind={ButtonKind.neutral}
          variant={ButtonVariant.regular}
          size={ButtonSize.small}
          onClick={handleEmailOptInClick}
        >
          <EmailOptInButtonTitle>
            <Content id="cid$#3869383#" /> {/* "Subscribe" */}
          </EmailOptInButtonTitle>
        </EmailOptInButton>
      </BannerContainer>
      <EmailOptInModal title={title} isOpen={isFormOpen} onModalClose={handleModalClose}>
        {contentView}
      </EmailOptInModal>
    </div>
  );
};
